import { useState, useEffect } from 'react';

/**
 * Custom hook that returns whether the viewport width is at or below a given max-width.
 *
 * Uses `window.matchMedia` for efficient, event-driven viewport detection.
 * Cleans up the listener on unmount to prevent memory leaks.
 *
 * @param maxWidth - The maximum width in pixels to match against
 * @returns `true` when viewport width ≤ maxWidth, `false` otherwise
 */
export function useMediaQuery(maxWidth: number): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);

    // Set initial value in case it changed between render and effect
    setMatches(mediaQuery.matches);

    function handleChange(event: MediaQueryListEvent) {
      setMatches(event.matches);
    }

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [maxWidth]);

  return matches;
}
